import { is_gen_2, isBluRC, isBluRotaryKnob, real_id, is_gen_z } from 'src/helpers/interface_helpers';
import { devices } from '../lists/devices';
import { devices_status } from '../device-runtime/status';
import {
	countZWaveInputChannels,
	deviceHasZWaveInputs,
	mapZWaveInputEventToReaderCode,
} from 'src/helpers/z_wave_status_helpers';
import type { MultipleEvents } from 'src/types/MultipleEvents';
import { derived } from 'svelte/store';

const isInputsReaderDevice = (device: { category?: string; mode?: string; type?: string }): boolean =>
	device.category === 'inputs_reader' ||
	(device.category === 'module' && device.mode === 'inputs_reader') ||
	device.category === 'remote_control' ||
	device.type === 'ZW-INPUT';

export const inputStates = derived([devices, devices_status], ([$devices, $devices_status]) => {
	let events = JSON.parse(sessionStorage.getItem('lastInputsStates')) || {};
	let multipleEvents: MultipleEvents = {};
	for (let id in $devices) {
		const device = $devices[id];
		const status = $devices_status[real_id(id)];
		const isWaveInput = is_gen_z(device) && deviceHasZWaveInputs(status);

		if (isInputsReaderDevice(device) || isWaveInput) {
			let event = '';
			let reporter;
			let time = +(new Date().getTime() / 1000).toFixed(0);

			if (typeof status?.inputs !== 'undefined') {
				event =
					status.inputs?.[device.channel]?.event !== ''
						? status.inputs?.[device.channel].event
						: event;
			} else if (is_gen_z(device)) {
				const channel = Number(device.channel) || 0;
				const vEve =
					status?.[`v_eve:${channel}`] ||
					status?.[`v_eve:${100 + channel}`] ||
					status?.['v_eve:0'];
				if (vEve?.ev) {
					event = mapZWaveInputEventToReaderCode(vEve.ev);
				} else if (vEve?.last_ev) {
					event = mapZWaveInputEventToReaderCode(vEve.last_ev);
					if (typeof vEve.last_ev_ts === 'number') {
						time = vEve.last_ev_ts / 1000;
					}
				}

				if (!multipleEvents[device.id] && countZWaveInputChannels(status) > 1) {
					multipleEvents[device.id] = [];
					Object.keys(status || {}).forEach((key) => {
						if (!key.startsWith('v_eve:')) return;
						const channelId = key.split(':')[1];
						multipleEvents[device.id].push({
							id: channelId,
							ev: mapZWaveInputEventToReaderCode(status[key].ev),
							last_ev: mapZWaveInputEventToReaderCode(status[key].last_ev),
							last_ev_ts: status[key].last_ev_ts,
						});
					});
					multipleEvents[device.id] = multipleEvents[device.id].sort(
						(a, b) => Number(a.id) - Number(b.id),
					);
				}
			} else if (is_gen_2(device)) {
				if (status?.['v_eve:' + device.channel]?.ev !== '') {
					event = status?.['v_eve:' + device.channel]?.ev;
				} else {
					event = status?.['v_eve:' + device.channel]?.last_ev;
					time = status?.['v_eve:' + device.channel]?.last_ev_ts / 1000;
				}
			} else if (device.gen == 'GBLE') {
				reporter = status?.reporter?.id !== '' ? status?.reporter : undefined;

				if (status?.['v_eve:' + device.channel]?.ev !== '') {
					event = status?.['v_eve:' + device.channel]?.ev;
				} else {
					event = status?.['v_eve:' + device.channel]?.last_ev;

					if (isBluRC(device.type) || isBluRotaryKnob(device.type)) {
						if (!multipleEvents[device.id]) {
							multipleEvents[device.id] = [];
						}
						Object.keys(status).forEach((key) => {
							// Handle button events (v_eve:) and scroll events (rotinput:)
							if (key.startsWith('v_eve:')) {
								const channel = key.split(':')[1];
								multipleEvents[device.id].push({
									id: channel,
									ev: status[key].ev,
									last_ev: status[key].last_ev,
									last_ev_ts: status[key].last_ev_ts,
								});
							} else if (key.startsWith('rotinput:')) {
								// Add scroll wheel events
								const channel = key.split(':')[1];
								multipleEvents[device.id].push({
									id: channel,
									ev: status[key].ev || '',
									last_ev: status[key].last_ev || '',
									last_ev_ts: status[key].last_ev_ts || 0,
									steps: status[key].steps || 0,
								});
							}
						});
						multipleEvents[device.id] = multipleEvents[device.id].sort(
							(a, b) => Number(a.id) - Number(b.id),
						);
					}
					time = status?.['v_eve:' + device.channel]?.last_ev_ts / 1000;
				}
			}

			if (typeof event !== 'undefined' && event !== '') {
				events = { ...events, [id]: { event, time, reporter } };
			}
			if (typeof multipleEvents[id] !== 'undefined' && multipleEvents[id].length > 0) {
				events = { ...events, [id]: multipleEvents[id] };
			}
		}
	}
	return events;
});

inputStates.subscribe((val) => sessionStorage.setItem('lastInputsStates', JSON.stringify(val)));
